import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { kebabCase } from "lodash-es";
import { type ElementType, type MouseEvent, useCallback, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { useAlerts } from "../Alerts/AlertProvider";

type BaseLinkNavItem = {
  text: string;
  path?: string;
  isDisabled?: boolean;
  textColor?: string;
  isExternal?: boolean;
  matchPaths?: string[];
};

export type StandardNavItem = BaseLinkNavItem & {
  kind: "Standard";
  Icon: ElementType;
};

export type GroupChildNavItem = BaseLinkNavItem & {
  kind: "GroupChild";
};

export type SectionNavItem = {
  kind: "Section";
  text: string;
};

export type GroupNavItem = {
  kind: "Group";
  text: string;
  Icon: ElementType;
  defaultPath: string;
  children: GroupChildNavItem[];
};

type NavItemProps = {
  navItem: StandardNavItem | GroupNavItem | GroupChildNavItem | SectionNavItem;
  collapsed: boolean;
};

const NAV_ITEM_BORDER_RADIUS = "8px";

function getInternalUrl(path: string | undefined, text: string) {
  const resolvedPath = path ?? text.split("/").map(kebabCase).join("/");
  return `/${resolvedPath}`;
}

function getMatchPaths(path: string | undefined, text: string, matchPaths: string[] = []) {
  return [
    getInternalUrl(path, text),
    ...matchPaths.map((matchPath) => getInternalUrl(matchPath, text)),
  ];
}

function isPathMatch(currentPath: string, candidatePath: string) {
  return currentPath === candidatePath || currentPath.startsWith(`${candidatePath}/`);
}

function isNavItemActive(
  currentPath: string,
  item: Pick<BaseLinkNavItem, "path" | "text" | "matchPaths" | "isExternal">
) {
  if (item.isExternal) {
    return false;
  }

  return getMatchPaths(item.path, item.text, item.matchPaths).some((candidatePath) =>
    isPathMatch(currentPath, candidatePath)
  );
}

function renderIconColor(isActive: boolean, textColor?: string) {
  if (textColor) {
    return textColor;
  }

  if (isActive) {
    return "white";
  }

  return "#8D98A6";
}

function renderLinkTextColor(isActive: boolean, textColor?: string) {
  if (textColor) {
    return textColor;
  }

  if (isActive) {
    return "white";
  }

  return "white";
}

function getNavItemUrl(navItem: BaseLinkNavItem) {
  if (navItem.isExternal) {
    return navItem.path ?? "";
  }

  return getInternalUrl(navItem.path, navItem.text);
}

function getNavItemTooltipTitle(navItem: Pick<BaseLinkNavItem, "text" | "isExternal">) {
  return navItem.isExternal ? `${navItem.text} (opens in new tab)` : navItem.text;
}

function handleDisabledNavItemClick(
  event: MouseEvent<HTMLAnchorElement>,
  navItem: Pick<BaseLinkNavItem, "isDisabled">,
  alerts: ReturnType<typeof useAlerts>
) {
  if (navItem.isDisabled) {
    event.preventDefault();
    alerts.info("This section is not yet available.");
  }
}

function StandardNavLink({ navItem, collapsed }: { navItem: StandardNavItem; collapsed: boolean }) {
  const alerts = useAlerts();
  const location = useLocation();
  const isActive = isNavItemActive(location.pathname, navItem);
  const iconColor = renderIconColor(isActive, navItem.textColor);
  const url = getNavItemUrl(navItem);
  const tooltipTitle = getNavItemTooltipTitle(navItem);

  if (collapsed) {
    return (
      <ListItem
        disablePadding
        sx={{
          display: "flex",
          justifyContent: "center",
          px: 1.5,
          py: 0.5,
        }}
      >
        <Tooltip
          title={tooltipTitle}
          placement="right"
          enterDelay={100}
          disableInteractive
          slotProps={{
            tooltip: {
              sx: {
                pointerEvents: "none",
              },
            },
          }}
        >
          <NavLink
            aria-label={tooltipTitle}
            onClick={(event) => {
              handleDisabledNavItemClick(event, navItem, alerts);
            }}
            to={url}
            target={navItem.isExternal ? "_blank" : undefined}
            rel={navItem.isExternal ? "noopener noreferrer" : undefined}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "block",
              width: "100%",
            }}
          >
            <ListItemButton
              sx={{
                minHeight: 44,
                minWidth: 44,
                justifyContent: "center",
                px: 0,
                py: 1,
                borderRadius: 999,
                backgroundColor: isActive ? "#517BC6" : "transparent",
                "&:hover": {
                  backgroundColor: isActive ? "#4A88D0" : "transparent",
                  ".MuiSvgIcon-root": {
                    color: isActive ? "white" : "#4A88D0",
                  },
                },
              }}
            >
              <navItem.Icon
                sx={{
                  fontSize: "24px",
                  color: iconColor,
                  opacity: navItem.textColor && !isActive ? 0.5 : 1,
                }}
              />
            </ListItemButton>
          </NavLink>
        </Tooltip>
      </ListItem>
    );
  }

  return (
    <ListItem disablePadding sx={{ display: "flex", height: "48px" }}>
      <NavLink
        aria-label={navItem.text}
        onClick={(event) => {
          handleDisabledNavItemClick(event, navItem, alerts);
        }}
        to={url}
        target={navItem.isExternal ? "_blank" : undefined}
        rel={navItem.isExternal ? "noopener noreferrer" : undefined}
        style={{
          textDecoration: "none",
          color: "inherit",
          width: "100%",
          marginLeft: "8px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          height: "32px",
        }}
      >
        <ListItemButton
          sx={{
            background: isActive ? "#4A88D0" : "none",
            pl: "16px",
            pr: 2,
            borderRadius: NAV_ITEM_BORDER_RADIUS,
            "&:hover": {
              backgroundColor: isActive ? "#4A88D0" : "transparent",
              ".MuiSvgIcon-root": {
                color: isActive ? "white" : "#4A88D0",
              },
              ".MuiTypography-root": {
                color: isActive ? "white" : "#4A88D0",
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 24,
              mr: "12px",
            }}
          >
            <navItem.Icon
              sx={{
                fontSize: "24px",
                color: iconColor,
                opacity: navItem.textColor && !isActive ? 0.5 : 1,
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={navItem.text}
            primaryTypographyProps={{
              fontSize: "16px",
              lineHeight: "24px",
              color: renderLinkTextColor(isActive, navItem.textColor),
              marginLeft: 0,
            }}
          />
        </ListItemButton>
      </NavLink>
    </ListItem>
  );
}

function GroupChildNavLink({ navItem }: { navItem: GroupChildNavItem }) {
  const alerts = useAlerts();
  const location = useLocation();
  const isActive = isNavItemActive(location.pathname, navItem);
  const url = getNavItemUrl(navItem);

  return (
    <ListItem
      disablePadding
      sx={{
        display: "flex",
        height: "48px",
        "&:hover": {
          ".MuiTypography-root": {
            color: isActive ? "white" : "#4A88D0",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "36px",
          height: "100%",
          p: 0,
          ml: "8px",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "32px",

            borderTopLeftRadius: NAV_ITEM_BORDER_RADIUS,
            borderBottomLeftRadius: NAV_ITEM_BORDER_RADIUS,
            background: isActive ? "#4A88D0" : "none",
          }}
        />
      </Box>
      <Box
        aria-hidden
        sx={{
          width: "1px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            flex: 1,
            backgroundColor: "#596376",
          }}
        />
        <Box
          sx={{
            width: "100%",
            height: "32px",
            flexShrink: 0,
            backgroundColor: isActive ? "white" : "#596376",
          }}
        />
        <Box
          sx={{
            width: "100%",
            flex: 1,
            backgroundColor: "#596376",
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minWidth: 0,
        }}
      >
        <NavLink
          aria-label={navItem.text}
          onClick={(event) => {
            handleDisabledNavItemClick(event, navItem, alerts);
          }}
          to={url}
          target={navItem.isExternal ? "_blank" : undefined}
          rel={navItem.isExternal ? "noopener noreferrer" : undefined}
          style={{
            textDecoration: "none",
            color: "inherit",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            flexDirection: "row",
            height: "32px",
            borderTopRightRadius: NAV_ITEM_BORDER_RADIUS,
            borderBottomRightRadius: NAV_ITEM_BORDER_RADIUS,
            background: isActive ? "#4A88D0" : "none",
          }}
        >
          <ListItemButton
            sx={{
              my: 0,
              py: 0,
              pl: 3,
              pr: 0,
              borderTopRightRadius: NAV_ITEM_BORDER_RADIUS,
              borderBottomRightRadius: NAV_ITEM_BORDER_RADIUS,
            }}
          >
            <ListItemText
              primary={navItem.text}
              primaryTypographyProps={{
                fontSize: "14px",
                lineHeight: "24px",
                color: renderLinkTextColor(isActive, navItem.textColor),
                marginLeft: 0,
              }}
            />
          </ListItemButton>
        </NavLink>
      </Box>
    </ListItem>
  );
}

function GroupNavRow({ navItem, collapsed }: { navItem: GroupNavItem; collapsed: boolean }) {
  const location = useLocation();
  const firstChild = navItem.children[0];
  const firstChildUrl = firstChild
    ? getNavItemUrl(firstChild)
    : getInternalUrl(navItem.defaultPath, navItem.text);
  const collapseId = `nav-group-${kebabCase(navItem.text)}`;
  const storageKey = `nav-menu-${navItem.text}-group-expanded`;

  const childIsActive = useMemo(() => {
    return navItem.children.some((child) => isNavItemActive(location.pathname, child));
  }, [location.pathname, navItem.children]);
  const [persistedExpanded, setPersistedExpanded] = useLocalStorageState<boolean>(storageKey, {
    defaultValue: false,
  });
  const [expanded, setExpanded] = useState(persistedExpanded);
  const groupShowsActiveState = childIsActive && (!expanded || collapsed);

  const updateExpanded = useCallback(
    (nextExpanded: boolean | ((currentExpanded: boolean) => boolean)) => {
      const resolvedExpanded =
        typeof nextExpanded === "function" ? nextExpanded(expanded) : nextExpanded;

      setExpanded(resolvedExpanded);
      setPersistedExpanded(resolvedExpanded);
    },
    [expanded, setPersistedExpanded]
  );

  if (collapsed) {
    const tooltipTitle = navItem.text;
    const url = getInternalUrl(navItem.defaultPath, navItem.text);

    return (
      <ListItem
        disablePadding
        sx={{
          display: "flex",
          justifyContent: "center",
          px: 1.5,
          py: 0.5,
        }}
      >
        <Tooltip
          title={tooltipTitle}
          placement="right"
          enterDelay={100}
          disableInteractive
          slotProps={{
            tooltip: {
              sx: {
                pointerEvents: "none",
              },
            },
          }}
        >
          <NavLink
            aria-label={tooltipTitle}
            to={url}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "block",
              width: "100%",
            }}
          >
            <ListItemButton
              selected={groupShowsActiveState}
              sx={{
                minHeight: 44,
                minWidth: 44,
                justifyContent: "center",
                px: 0,
                py: 1,
                borderRadius: 999,
                "&.Mui-selected": {
                  backgroundColor: "#517BC6",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "#4A88D0",
                },
                "&:hover": {
                  backgroundColor: groupShowsActiveState ? "#4A88D0" : "none",
                  ".MuiSvgIcon-root": {
                    color: groupShowsActiveState ? "white" : "#4A88D0",
                  },
                },
              }}
            >
              <navItem.Icon
                sx={{
                  fontSize: "24px",
                  color: renderIconColor(groupShowsActiveState),
                }}
              />
            </ListItemButton>
          </NavLink>
        </Tooltip>
      </ListItem>
    );
  }

  return (
    <ListItem disablePadding sx={{ display: "block" }}>
      <Box
        sx={{
          p: 0,
          my: "8px",
          ml: "8px",
          height: "38px",
          display: "flex",
          alignItems: "center",
          "&:hover": {
            backgroundColor: groupShowsActiveState ? "#4A88D0" : "none",
            ".MuiSvgIcon-root": {
              color: groupShowsActiveState ? "white" : "#517BC6",
            },
            ".MuiTypography-root": {
              color: groupShowsActiveState ? "white" : "#517BC6",
            },
          },
          background: groupShowsActiveState ? "#4A88D0" : "none",
          borderRadius: NAV_ITEM_BORDER_RADIUS,
        }}
      >
        <ListItemIcon
          sx={{
            pl: "16px",
            minWidth: 24,
            mr: "12px",
          }}
        >
          <navItem.Icon
            sx={{
              color: renderIconColor(groupShowsActiveState),
            }}
          />
        </ListItemIcon>
        <NavLink
          aria-label={navItem.text}
          onClick={() => {
            updateExpanded(true);
          }}
          to={firstChildUrl}
          style={{
            textDecoration: "none",
            color: "inherit",
            minWidth: 0,
          }}
        >
          <ListItemButton
            sx={{
              p: 0,
              minHeight: "38px",
              minWidth: 0,
              borderRadius: NAV_ITEM_BORDER_RADIUS,
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <ListItemText
              primary={navItem.text}
              slotProps={{
                primary: {
                  sx: {
                    fontSize: "16px",
                    color: "white",
                    ml: 0,
                  },
                },
              }}
            />
          </ListItemButton>
        </NavLink>

        <Box sx={{ flex: 1 }} />
        <IconButton
          aria-controls={collapseId}
          aria-expanded={expanded}
          aria-label={`${expanded ? "Collapse" : "Expand"} ${navItem.text}`}
          onClick={() => {
            updateExpanded((current) => !current);
          }}
          size="small"
          sx={{
            mr: "4px",
            borderRadius: NAV_ITEM_BORDER_RADIUS,
          }}
        >
          <ExpandMore
            sx={{
              color: groupShowsActiveState ? "white" : "#8D98A6",
              transform: expanded ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          />
        </IconButton>
      </Box>
      <Collapse id={collapseId} in={expanded} timeout="auto" unmountOnExit>
        <List disablePadding>
          {navItem.children.map((child) => (
            <GroupChildNavLink key={`${navItem.text}-${child.text}`} navItem={child} />
          ))}
        </List>
      </Collapse>
    </ListItem>
  );
}

function SectionNavRow({ navItem, collapsed }: { navItem: SectionNavItem; collapsed: boolean }) {
  if (collapsed) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1.5,
        }}
      >
        <Box
          sx={{
            height: "1px",
            width: "100%",
            backgroundColor: "#596376",
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        pl: "16px",
        pr: "4px",
        py: "12px",
        pb: "0px",
      }}
    >
      <Typography
        sx={{
          color: "white",
          fontSize: "12px",
          fontWeight: 400,
          fontStyle: "normal",
          lineHeight: "24px",
          letterSpacing: "1px",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {navItem.text}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: "2px",
          backgroundColor: "#878D99",
          minWidth: 0,
        }}
      />
    </Box>
  );
}

export function NavItemV2({ navItem, collapsed }: NavItemProps) {
  if (navItem.kind === "Section") {
    return <SectionNavRow navItem={navItem} collapsed={collapsed} />;
  }

  if (navItem.kind === "Group") {
    return <GroupNavRow navItem={navItem} collapsed={collapsed} />;
  }

  if (navItem.kind === "Standard") {
    return <StandardNavLink navItem={navItem} collapsed={collapsed} />;
  }

  return <GroupChildNavLink navItem={navItem} />;
}
